import * as React from 'react'

import {IFeatureLayerInfo} from '../../interfaces/models'
import {IFieldInfo, IUser} from '../../interfaces/reducers'
import OverlayLoader from '../Loading/OverlayLoading'

import DatePicker from 'react-datepicker'
import Select from 'react-select'
import {v4 as uuidv4} from 'uuid'
import {ComponentTarget} from '../../enums/ComponentTarget'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {DrawSource} from '../../enums/DrawSource'
import {GeometryType} from '../../enums/GeometryType'
import {IEditsDispatcher, IGeometryEditsDispatcher} from '../../interfaces/dispatchers'
import {ISourceApplyEdits} from '../../interfaces/services'
import {Feature, FeatureStatus, Geometry} from '../../models'
import {getMapService} from '../../reducers/map'
import {ServiceHub} from '../../ServiceHub'
import {AttachmentsViewer} from '../Controls/Attachments/AttachmentsViewer2'
import GeometryEditor from './GeometryEditor'

export interface Props {
  feature: Feature
  layerInfo: IFeatureLayerInfo
  onFeatureChanged?: (feature: Feature) => void
  editsDispatcher?: IEditsDispatcher
  geometryEditsDispatcher?: IGeometryEditsDispatcher
  onBeforeSave?: (feature: Feature, continueSave: (feature: Feature) => void) => void
  onAfterSave?: (feature: Feature) => void
  onCancel?: (feature: Feature) => void
  onBeforeDelete?: (feature: Feature, continueDelete: (feature: Feature) => void) => void
  onAfterDelete?: (feature: Feature) => void
  editAgain: (feature: Feature) => Feature
  style?: React.CSSProperties
  buttonsClassname?: string
  saveControlsTop?: boolean
  hideCancel?: boolean
  hideDelete?: boolean
  hideSave?: boolean
  user?: IUser
  showTransformSelector?: boolean
  hideGeometrySelector?: boolean
  map?: JSX.Element
}

export interface State {
  loading: boolean
  maxHeight: string
  feature: Feature
  showGuiltyFields?: boolean
  errorMessage: string
  success?: boolean
}

export enum ControlError {
  None = 'None',
  TooLong = 'TooLong',
  Required = 'Required',
  Custom = 'Custom'
}

class FeatureEditor extends React.Component<Props, State> {
  editingGuid: string = uuidv4()
  constructor(props: Props) {
    super(props)
    this.state = {
      maxHeight: '0px',
      loading: true,
      feature: props.feature.clone(),
      errorMessage: ''
    }

    this.onFieldValueChange = this.onFieldValueChange.bind(this)
    this.onGeometryChange = this.onGeometryChange.bind(this)
    this.save = this.save.bind(this)
    this.trySave = this.trySave.bind(this)

    this.tryDelete = this.tryDelete.bind(this)
    this.delete = this.delete.bind(this)
    this.onError = this.onError.bind(this)
  }

  componentDidMount() {
    this.loadDependencies(this.props)
    const mapService = getMapService()
    if (mapService?.drawing) {
      mapService.drawing.unMark(DrawSource.Select)
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.feature !== this.props.feature) {
      this.loadDependencies(this.props)
    }
  }

  componentWillUnmount() {
    if (this.props.editsDispatcher)
      this.props.editsDispatcher.cancel({
        [this.props.layerInfo.url]: {[this.props.feature.getId()]: this.props.feature}
      })
    const mapService = getMapService()
    if (this.props.geometryEditsDispatcher && mapService?.editing) {
      mapService.editing.unsubscribeForChanges(this.editingGuid)
      mapService.editing.ejectFromEditing({
        [this.props.layerInfo.url]: {[this.props.feature.getId()]: this.props.feature}
      })
    }

    if (mapService?.drawing) {
      mapService.drawing.deactivateDraw(true)
    }
  }
  mountedSetOriginals = false
  protected loadDependencies(props: Props) {
    if (props.editsDispatcher) {
      const isDifferentFeature =
        props.feature.getId() != this.props.feature.getId() ||
        props.layerInfo.sourceDescription.id != this.props.layerInfo.sourceDescription.id
      if (!this.mountedSetOriginals || isDifferentFeature) {
        if (isDifferentFeature) {
          props.editsDispatcher.cancel({[this.props.layerInfo.url]: {[this.props.feature.getId()]: this.props.feature}})
          const mapService = getMapService()
          if (mapService?.editing) {
            mapService.editing.ejectFromEditing({
              [this.props.layerInfo.url]: {[this.props.feature.getId()]: this.props.feature}
            })
          }
          if (mapService?.drawing) {
            mapService.drawing.deactivateDraw(true)
          }
        }
        this.mountedSetOriginals = true
        props.editsDispatcher.setOriginals({[props.layerInfo.url]: {[props.feature.getId()]: props.feature}}, () => {
          if (props.geometryEditsDispatcher) {
            const mapService = getMapService()
            if (!mapService?.editing) return
            const mapEditingService = mapService.editing
            mapEditingService.setForEditing({[props.layerInfo.url]: {[props.feature.getId()]: props.feature}})
            mapEditingService.subscribeForChanges(this.editingGuid, changes => {
              const changesBlock = changes[props.layerInfo.url]
              if (changesBlock) {
                const changedFeat = changesBlock[props.feature.getId()]
                if (changedFeat) {
                  this.props.feature.setGeometry(changedFeat.getGeometry())
                  this.applyChanges(this.props.feature)
                }
              }
            })
          }
        })
      }
      this.setState({
        ...this.state,
        feature: props.feature.clone(),
        loading: false
      })
    } else {
      // when there is not editsDispatcher and we receive props this means a new edit is coming, we have to default everything
      this.setState({
        ...this.state,
        loading: false,
        errorMessage: '',
        feature: props.feature.clone(),
        success: false,
        showGuiltyFields: false
      })
    }
  }

  applyChanges(feature: Feature) {
    if (this.props.onFeatureChanged) {
      this.props.onFeatureChanged(feature)
    }
    if (this.props.editsDispatcher) {
      this.props.editsDispatcher.applyChanges(
        {[this.props.layerInfo.url]: {[feature.getId()]: feature}},
        ComponentTarget.Map | ComponentTarget.SingleCreator | ComponentTarget.SingleView | ComponentTarget.Popup
      )
    } else {
      this.setState({...this.state, feature: feature.clone()})
    }
  }

  protected isEditable(layerInfo: IFeatureLayerInfo, feature: Feature, fieldInfo: IFieldInfo): boolean {
    return !!fieldInfo.editable
  }

  onSuccessSave(feature: Feature) {
    this.setState({...this.state, loading: false, errorMessage: '', success: true}, () => {
      if (this.props.onAfterSave) {
        this.props.onAfterSave(feature)
      }
    })
  }

  protected save(feature: Feature): void {
    this.setState({...this.state, loading: true, errorMessage: ''})
    let paylod: ISourceApplyEdits[] = [{layerInfo: this.props.layerInfo, adds: [], updates: [feature], deletes: []}]
    let isNew = false
    if (feature.status == FeatureStatus.New) {
      isNew = true
      paylod = [{layerInfo: this.props.layerInfo, adds: [feature], updates: [], deletes: []}]
    }
    let promise: Promise<ISourceApplyEdits[]>
    if (this.props.editsDispatcher) {
      promise = new Promise((resolve, reject) => {
        this.props.editsDispatcher!.applyEdits(
          paylod,
          edits => {
            resolve(edits)
          },
          e => {
            reject(e)
            this.onError(e)
          }
        )
      })
    } else {
      promise = ServiceHub.gisCrudService.applyEdits(paylod)
    }
    promise
      .then(edits => {
        this.props.layerInfo.refresh()
        this.onSuccessSave(feature)
        if (isNew) {
          this.props.editAgain(feature)
        }
      })
      .catch(error => {
        this.onError(error)
      })
  }

  protected onError(error: any) {
    if (error.response && error.response.data) {
      this.setState({...this.state, loading: false, errorMessage: 'Възникна грешка: ' + error.response.data})
    } else {
      this.setState({...this.state, loading: false, errorMessage: 'Възникна грешка'})
    }

    console.error(error)
  }

  protected delete(feature: Feature): void {
    this.setState({...this.state, loading: true, errorMessage: ''})
    let promise: Promise<ISourceApplyEdits[]>
    const paylod: ISourceApplyEdits[] = [{layerInfo: this.props.layerInfo, adds: [], updates: [], deletes: [feature]}]
    if (this.props.editsDispatcher) {
      promise = new Promise((resolve, reject) => {
        this.props.editsDispatcher!.applyEdits(
          paylod,
          edits => {
            resolve(edits)
          },
          e => {
            reject(e)
            this.onError(e)
          }
        )
      })
    } else {
      promise = ServiceHub.gisCrudService.applyEdits(paylod)
    }
    promise
      .then(result => {
        this.props.layerInfo.refresh()
        this.setState({...this.state, loading: false})
        if (this.props.onAfterDelete) {
          this.props.onAfterDelete(feature)
        }
      })
      .catch(error => {
        this.onError(error)
      })
  }

  protected onFieldValueChange(fieldName: string, feature: Feature, fieldInfo: IFieldInfo, newValue: any) {
    feature.set(fieldName, newValue)
    this.applyChanges(feature)
  }

  protected getNomenclatureOptions(fieldInfo: IFieldInfo): Array<JSX.Element> {
    const options: Array<JSX.Element> = []
    if (fieldInfo.nullable) {
      options.push(<option key={'emptySelection'}></option>)
    }
    fieldInfo.codedValues!.forEach((d, i) => {
      options.push(
        <option key={i} value={d.code}>
          {d.name}
        </option>
      )
    })
    return options
  }

  protected getNomenclatureEditingControl(
    currentValue: React.ReactText | undefined,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    error: ControlError
  ) {
    return this.getControlStar(
      <select
        className="form-control"
        value={currentValue !== undefined && currentValue !== null && currentValue !== '' ? currentValue : ''}
        onChange={e => {
          this.onFieldValueChange(fieldName, feature, fieldInfo, e.target.value)
        }}
      >
        {this.getNomenclatureOptions(fieldInfo)}
      </select>,
      fieldInfo
    )
  }

  protected getEnumMultiSelectEditingControl(
    currentValue: React.ReactText | undefined,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    error: ControlError
  ) {
    const options: {value: any; label: any}[] = []
    const selected: {value: any; label: any}[] = []
    fieldInfo.codedValues!.forEach(c => {
      options.push({value: c.code, label: c.name})
      if ((c.code as number) & (currentValue as number)) {
        selected.push({value: c.code, label: c.name})
      }
    })
    return this.getControlStar(
      <Select
        className="w"
        isMulti={true}
        closeMenuOnSelect={false}
        value={selected}
        onChange={(newValues: {value: any; label: any}[]) => {
          let newValue = 0
          newValues.forEach(v => {
            newValue = newValue | v.value
          })
          this.onFieldValueChange(fieldName, feature, fieldInfo, newValue)
        }}
        options={options}
      />,
      fieldInfo
    )
  }

  protected getStringEditingControl(
    currentValue: React.ReactText | undefined,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    error: ControlError
  ) {
    let input = (
      <input
        className="form-control"
        type="text"
        value={currentValue === undefined || currentValue === null ? '' : currentValue}
        onChange={e => {
          this.onFieldValueChange(fieldName, feature, fieldInfo, e.target.value)
        }}
      ></input>
    )
    if (fieldInfo.length && fieldInfo.length > 255) {
      input = (
        <textarea
          style={{width: '100%', resize: 'vertical'}}
          className="form-control"
          rows={4}
          value={currentValue === undefined || currentValue === null ? '' : currentValue}
          onChange={e => {
            this.onFieldValueChange(fieldName, feature, fieldInfo, e.target.value)
          }}
        ></textarea>
      )
    }
    const noErrorContent = this.getControlStar(input, fieldInfo)
    return this.wrapGuiltyControl(noErrorContent, error, fieldInfo)
  }

  protected getNumberEditingControl(
    currentValue: React.ReactText | undefined,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    error: ControlError
  ) {
    const noErrorContent = this.getControlStar(
      <input
        className="form-control"
        type="number"
        pattern="\d*"
        value={currentValue === undefined || currentValue === null ? '' : currentValue}
        onChange={e => {
          let value = e.target.value as any
          if (value) {
            if (fieldInfo.type == 'integer' || fieldInfo.type == 'long') {
              value = parseInt(value)
            } else {
              value = parseFloat(value)
            }
          }

          this.onFieldValueChange(fieldName, feature, fieldInfo, value)
        }}
      ></input>,
      fieldInfo
    )
    return this.wrapGuiltyControl(noErrorContent, error, fieldInfo)
  }

  protected handleDateChange(fieldName: string, feature: Feature, fieldInfo: IFieldInfo, changedValue: any) {
    if (changedValue) {
      const newDate = new Date(changedValue)
      if (fieldInfo.forceTime) {
        fieldInfo.forceTime.forEach((v, i) => {
          if (v == undefined) {
            return
          }
          if (i == 0) {
            newDate.setHours(v)
          } else if (i == 1) {
            newDate.setMinutes(v)
          } else if (i == 2) {
            newDate.setSeconds(v)
          } else if (i == 3) {
            newDate.setMilliseconds(v)
          }
        })
      }
      if (fieldInfo.forceDate) {
        fieldInfo.forceDate.forEach((v, i) => {
          if (v == undefined) {
            return
          }
          if (i == 0) {
            newDate.setFullYear(v)
          } else if (i == 1) {
            newDate.setMonth(v - 1)
          } else if (i == 2) {
            newDate.setDate(v)
          }
        })
      }
      this.onFieldValueChange(fieldName, feature, fieldInfo, newDate.getTime())
    } else {
      this.onFieldValueChange(fieldName, feature, fieldInfo, changedValue)
    }
  }

  protected getDateEditingDatePicker(
    dateFormat: string,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    date: Date | undefined
  ): JSX.Element {
    return (
      <DatePicker
        showTimeSelect={dateFormat.indexOf('HH:mm') != -1}
        showTimeSelectOnly={dateFormat == 'HH:mm'}
        timeFormat="HH:mm"
        timeIntervals={15}
        dateFormat={dateFormat}
        className="form-control"
        selected={date}
        onChange={v => {
          this.handleDateChange(fieldName, feature, fieldInfo, v)
        }}
        isClearable={fieldInfo.nullable}
        showYearDropdown
      />
    )
  }

  protected getDateEditingControl(
    currentValue: React.ReactText | undefined,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    error: ControlError
  ) {
    let date: Date | undefined
    if (currentValue) {
      date = new Date(currentValue)
    }
    let dateFormat = 'dd.MM.yyyy'
    if (fieldInfo.dateFormat) {
      dateFormat = fieldInfo.dateFormat
    }
    const noErrorContent = this.getControlStar(
      this.getDateEditingDatePicker(dateFormat, fieldName, feature, fieldInfo, date),
      fieldInfo
    )
    return this.wrapGuiltyControl(noErrorContent, error, fieldInfo)
  }

  protected getControlStar(control: JSX.Element, fieldInfo: IFieldInfo): JSX.Element {
    if (!fieldInfo.codedValues && !fieldInfo.nullable) {
      return (
        <div style={{display: 'flex'}}>
          {control}
          <div style={{fontSize: '16px', width: '10px'}}>*</div>
        </div>
      )
    }
    return (
      <div style={{display: 'flex'}}>
        {control}
        <div style={{fontSize: '16px', width: '10px'}}></div>
      </div>
    )
  }

  protected controlsHaveErrors(feature: Feature): boolean {
    let haveErrors = false
    const atts = feature.getProperties()
    for (const key in atts) {
      if (
        this.props.layerInfo.fields[key] &&
        this.controlHasErrors(atts[key], this.props.layerInfo.fields[key]) != ControlError.None
      ) {
        haveErrors = true
        break
      }
    }
    return haveErrors
  }

  trySave(feature: Feature) {
    if (this.controlsHaveErrors(feature)) {
      this.setState({...this.state, showGuiltyFields: true})
    } else {
      if (this.props.onBeforeSave) {
        this.props.onBeforeSave(feature, feat => {
          this.save(feat)
        })
      } else {
        this.save(feature)
      }
    }
  }

  protected controlHasErrors(currentValue: React.ReactText | undefined, fieldInfo: IFieldInfo): ControlError {
    if (!fieldInfo.nullable && (currentValue === '' || currentValue === null || currentValue === undefined)) {
      return ControlError.Required
    } else if (
      fieldInfo.type == 'string' &&
      fieldInfo.length &&
      currentValue &&
      (currentValue as string).length > fieldInfo.length
    ) {
      return ControlError.TooLong
    }
    return ControlError.None
  }

  protected wrapGuiltyControl(
    guiltyControl: JSX.Element,
    controlErrorType: ControlError,
    fieldInfo: IFieldInfo
  ): JSX.Element {
    return (
      <div>
        <div
          style={{
            color: 'red',
            display: controlErrorType == ControlError.None || !this.state.showGuiltyFields ? 'none' : 'block'
          }}
        >
          {controlErrorType == ControlError.Required
            ? 'Полето е задължително'
            : `Дължината на полето трябва да е по - голяма от ${fieldInfo.length}`}
        </div>
        {guiltyControl}
      </div>
    )
  }

  protected getReadonlyControl(
    currentValue: React.ReactText | undefined,
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo
  ) {
    if (fieldInfo.codedValues) {
      return (
        <input
          className="form-control"
          readOnly={true}
          value={fieldInfo.codedValues!.find(d => d.code == currentValue)!.name || ''}
          onChange={e => {}}
        />
      )
    } else if (fieldInfo.type == 'date' && currentValue) {
      const dateVal = new Date(currentValue!)
      return (
        <input
          className="form-control"
          readOnly={true}
          value={`${dateVal.getFullYear()}.${dateVal.getMonth() + 1}.${dateVal.getDate()}` || ''}
          onChange={e => {}}
        />
      )
    } else {
      return (
        <input
          className="form-control"
          readOnly={true}
          value={currentValue === undefined || currentValue === null ? '' : currentValue}
          onChange={e => {}}
        />
      )
    }
  }

  protected getEditingControl(
    fieldName: string,
    feature: Feature,
    fieldInfo: IFieldInfo,
    layerInfo: IFeatureLayerInfo,
    error: ControlError,
    currentValue?: React.ReactText
  ): JSX.Element {
    if (this.isEditable(layerInfo, feature, fieldInfo)) {
      if (fieldInfo.codedValues && fieldInfo.type == 'enum') {
        return this.getEnumMultiSelectEditingControl(currentValue, fieldName, feature, fieldInfo, error)
      } else if (fieldInfo.codedValues) {
        return this.getNomenclatureEditingControl(currentValue, fieldName, feature, fieldInfo, error)
      } else if (fieldInfo.type == 'date') {
        return this.getDateEditingControl(currentValue, fieldName, feature, fieldInfo, error)
      } else if (fieldInfo.type == 'string') {
        return this.getStringEditingControl(currentValue, fieldName, feature, fieldInfo, error)
      } else {
        return this.getNumberEditingControl(currentValue, fieldName, feature, fieldInfo, error)
      }
    } else {
      return this.getReadonlyControl(currentValue, fieldName, feature, fieldInfo)
    }
  }

  protected processField(fieldInfo: IFieldInfo, layerInfo: IFeatureLayerInfo, feature: Feature): boolean {
    return !!fieldInfo.editable
  }

  protected renderFeatures(feature: Feature, layerInfo: IFeatureLayerInfo): Array<JSX.Element> {
    const fields: Array<JSX.Element> = []
    const atts = feature.getProperties()
    for (const fieldName in layerInfo.fields) {
      const fieldInfo = layerInfo.fields[fieldName]
      if (this.processField(fieldInfo, layerInfo, feature)) {
        const currentValue = atts[fieldName] !== null || atts[fieldName] !== undefined ? atts[fieldName] : ''
        const error = this.controlHasErrors(currentValue, fieldInfo)

        if (fieldInfo.fieldName !== 'EKATTE') {
          fields.push(
            <div
              key={fieldName}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: error != ControlError.None && this.state.showGuiltyFields ? '#ff00000d' : '',
                padding: '4px'
              }}
            >
              <div className="detail-line-td">{fieldInfo.label}</div>
              <div className="detail-line-td">
                {this.getEditingControl(fieldName, feature, fieldInfo, layerInfo, error, currentValue)}
              </div>
            </div>
          )
        }
      }
    }
    return fields
  }

  protected tryDelete(feature: Feature) {
    if (this.props.onBeforeDelete) {
      this.props.onBeforeDelete(feature, feat => {
        this.delete(feat)
      })
    } else {
      this.delete(feature)
    }
  }

  protected isSaveDisabled(feature: Feature): boolean {
    return false
  }

  getSaveControls(feature: Feature): JSX.Element {
    return (
      <div style={{display: 'flex'}}>
        {!this.props.hideSave ? (
          <button
            type="button"
            disabled={this.isSaveDisabled(feature)}
            className={this.props.buttonsClassname || 'button margin width'}
            onClick={() => this.trySave(feature)}
          >
            Запис
          </button>
        ) : null}
        {this.props.onCancel && !this.props.hideCancel ? (
          <button
            type="button"
            className={this.props.buttonsClassname || 'button margin width'}
            onClick={() => this.props.onCancel!(feature)}
          >
            Отказ
          </button>
        ) : null}
        {!this.props.hideDelete ? (
          <button
            type="button"
            className={this.props.buttonsClassname || 'button margin width'}
            onClick={() => this.tryDelete(feature)}
          >
            Изтрий
          </button>
        ) : null}
      </div>
    )
  }

  protected sortFields(fields: Array<JSX.Element>, layerInfo: IFeatureLayerInfo): Array<JSX.Element> {
    const fieldInfos: IFieldInfo[] = []
    for (const fn in layerInfo.fields) {
      fieldInfos.push(layerInfo.fields[fn])
    }
    const fieldNames: string[] = fieldInfos.sort((a, b) => (a.z > b.z ? -1 : 1)).map(fi => fi.fieldName)

    if (fieldNames && fieldNames.length > 0) {
      const sorted: Array<JSX.Element> = fields.sort((a, b) => {
        if (a.key && b.key) {
          return fieldNames.indexOf((a as any).key) > fieldNames.indexOf((b as any).key) ? 1 : -1
        }
        return 0
      })
      return sorted
    }
    return fields
  }

  protected renderEditingBody(feature: Feature, layerInfo: IFeatureLayerInfo): JSX.Element {
    return <React.Fragment>{this.sortFields(this.renderFeatures(feature, layerInfo), layerInfo)}</React.Fragment>
  }
  protected onGeometryChange(feature: Feature, g?: Geometry): void {
    feature.setGeometry(g)
    if (this.props.geometryEditsDispatcher) {
      getMapService().editing.externalUpdate({[this.props.layerInfo.url]: {[this.props.feature.getId()]: feature}})
    } else {
      this.applyChanges(feature)
    }
  }

  protected getGeometryEditor(feature: Feature): JSX.Element {
    return (
      <GeometryEditor
        info={this.props.layerInfo.url}
        defaultDisplayCs={CoordinateSystem.BG2005CAD}
        showTransformSelector={this.props.showTransformSelector}
        feature={feature}
        geometryType={this.props.layerInfo.getGeometryType()}
        buttonsClassname={this.props.buttonsClassname}
        onGeometryChange={g => this.onGeometryChange(feature, g)}
      />
    )
  }

  protected getAttachmentsViewer(feature: Feature): JSX.Element {
    return (
      <AttachmentsViewer
        readonly={this.props.layerInfo.sourceDescription.attachmentsReadOnly}
        adds={feature.attachmentChanges.adds}
        multiple={true}
        updates={feature.attachmentChanges.updates}
        deletes={feature.attachmentChanges.deletes}
        attachments={feature.attachments}
        onAttachmentsChange={changes => {
          feature.attachmentChanges = changes
          this.applyChanges(feature)
        }}
        feature={feature}
        uploadToArchive={true}
      />
    )
  }

  render() {
    const feature = this.props.editsDispatcher ? this.props.feature : this.state.feature
    if (this.state.success) {
      return (
        <div style={{padding: '10px', boxSizing: 'border-box'}}>
          <div className="success-div">Записът беше успешен</div>
          <button
            type="button"
            className={this.props.buttonsClassname || 'button width'}
            onClick={() => {
              this.setState({...this.state, success: false})
            }}
          >
            {feature.status == FeatureStatus.New ? 'Създай нов' : 'Редактирай отново'}
          </button>
        </div>
      )
    }
    return (
      <div style={{...this.props.style, position: 'relative', height: '100%', overflow: 'auto'}}>
        <div style={{margin: '10px'}}>
          <OverlayLoader size="60px" show={this.state.loading} />
          {this.props.map}
          {this.props.saveControlsTop ? this.getSaveControls(feature) : null}
          {this.renderEditingBody(feature, this.props.layerInfo)}
          {this.props.layerInfo.getGeometryType() != GeometryType.None && !this.props.hideGeometrySelector
            ? this.getGeometryEditor(feature)
            : null}
          {this.props.layerInfo.sourceDescription.hasAttachments ? this.getAttachmentsViewer(feature) : null}
          {this.state.showGuiltyFields && this.controlsHaveErrors(feature) ? (
            <div className="error-div">Моля, оправете грешките</div>
          ) : null}
          {this.state.errorMessage ? <div className="error-div">{this.state.errorMessage}</div> : null}
          {this.props.saveControlsTop ? null : this.getSaveControls(feature)}
          <br></br>
        </div>
      </div>
    )
  }
}

export default FeatureEditor
